/**
 * Session Question Service
 * Manages question tracking within a quiz session to prevent repetition
 * and provides smart question selection with history awareness
 */

import { supabase } from '../../lib/supabase';
import { normalizeQuestionLocale } from '../utils/questionLocales';

export interface SessionQuestion {
  questionId: number;
  categoryId: number;
  difficultyId: number;
  questionType: string;
  answeredAt: Date;
  answeredCorrectly?: boolean;
  timeSpent?: number;
  score?: number;
}

export interface QuizSessionData {
  sessionId: string;
  userId: string;
  categoryId: number;
  difficultyId: number;
  totalQuestions: number;
  startedAt: Date;
  isMultiplayer: boolean;
  roomCode?: string;
}

export interface QuestionHistoryEntry {
  questionId: number;
  lastSeenDaysAgo: number | null;
  timesAnswered: number;
  selectionWeight: number;
}

class SessionQuestionService {
  private currentSession: QuizSessionData | null = null;
  private sessionQuestions: Map<number, SessionQuestion> = new Map();
  private sessionQuestionIds: Set<number> = new Set();
  private maxSessionSize = 100; // Maximum questions to track in a session

  /**
   * Start a new quiz session
   */
  async startSession(
    userId: string,
    categoryId: number,
    difficultyId: number,
    totalQuestions: number,
    isMultiplayer: boolean = false,
    roomCode?: string
  ): Promise<string> {
    try {
      // Clear any existing session
      this.clearSession();

      // Create session in database
      const { data, error } = await supabase.rpc('start_quiz_session', {
        p_user_id: userId,
        p_category_id: categoryId,
        p_difficulty_id: difficultyId,
        p_total_questions: totalQuestions,
        p_is_multiplayer: isMultiplayer,
        p_room_code: roomCode
      });

      if (error) {
        console.error('Error starting quiz session:', error);
        // Create a local session ID as fallback
        const localSessionId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        this.currentSession = {
          sessionId: localSessionId,
          userId,
          categoryId,
          difficultyId,
          totalQuestions,
          startedAt: new Date(),
          isMultiplayer,
          roomCode
        };
        
        return localSessionId;
      }

      this.currentSession = {
        sessionId: data,
        userId,
        categoryId,
        difficultyId,
        totalQuestions,
        startedAt: new Date(),
        isMultiplayer,
        roomCode
      };

      return data;
    } catch (error) {
      console.error('Error in startSession:', error);
      // Return a local session ID as fallback
      const localSessionId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      this.currentSession = {
        sessionId: localSessionId,
        userId,
        categoryId,
        difficultyId,
        totalQuestions,
        startedAt: new Date(),
        isMultiplayer,
        roomCode
      };
      
      return localSessionId;
    }
  }

  /**
   * Add a question to the current session
   */
  addQuestionToSession(
    questionId: number,
    categoryId: number,
    difficultyId: number,
    questionType: string
  ): void {
    // Limit session size to prevent memory issues
    if (this.sessionQuestions.size >= this.maxSessionSize) {
      // Remove oldest question
      const oldestKey = this.sessionQuestions.keys().next().value;
      if (oldestKey) {
        this.sessionQuestions.delete(oldestKey);
        this.sessionQuestionIds.delete(oldestKey);
      }
    }

    const question: SessionQuestion = {
      questionId,
      categoryId,
      difficultyId,
      questionType,
      answeredAt: new Date()
    };

    this.sessionQuestions.set(questionId, question);
    this.sessionQuestionIds.add(questionId);
  }

  /**
   * Record a question answer
   */
  async recordAnswer(
    questionId: number,
    answeredCorrectly: boolean,
    timeSpent?: number,
    score: number = 1.0,
    questionType?: string
  ): Promise<void> {
    // Update session tracking
    const sessionQuestion = this.sessionQuestions.get(questionId);
    if (sessionQuestion) {
      sessionQuestion.answeredCorrectly = answeredCorrectly;
      sessionQuestion.timeSpent = timeSpent;
      sessionQuestion.score = score;
    }

    // Record to database if we have a valid session
    if (this.currentSession && !this.currentSession.sessionId.startsWith('local_')) {
      try {
        await supabase.rpc('record_question_attempt', {
          p_user_id: this.currentSession.userId,
          p_question_id: questionId,
          p_category_id: this.currentSession.categoryId,
          p_difficulty_id: this.currentSession.difficultyId,
          p_question_type: questionType || sessionQuestion?.questionType || 'mcq_single',
          p_answered_correctly: answeredCorrectly,
          p_time_taken: timeSpent || null,
          p_score_earned: score,
          p_session_id: this.currentSession.sessionId
        });
      } catch (error) {
        console.error('Error recording answer to database:', error);
        // Continue even if database recording fails
      }
    }
  }

  /**
   * Get questions with history awareness and session exclusion
   */
  async getSmartQuestions(
    userId: string,
    categoryId: number,
    difficultyId: number,
    languageCode: string,
    limit: number
  ): Promise<any[]> {
    try {
      // Get array of session question IDs for exclusion
      const sessionQuestionArray = Array.from(this.sessionQuestionIds);

      // Call the smart selection RPC function
      const normalizedLanguageCode = normalizeQuestionLocale(languageCode);

      const { data, error } = await supabase.rpc('get_questions_with_history', {
        p_user_id: userId,
        p_category_id: categoryId,
        p_difficulty_id: difficultyId,
        p_language_code: normalizedLanguageCode,
        p_limit: limit,
        p_session_questions: sessionQuestionArray
      });

      if (error) {
        console.error('Error getting smart questions:', error);
        // Fall back to regular selection
        return this.getFallbackQuestions(categoryId, difficultyId, normalizedLanguageCode, limit);
      }

      // Add selected questions to session tracking
      data?.forEach((q: any) => {
        this.addQuestionToSession(
          q.question_id,
          categoryId,
          difficultyId,
          q.question_type
        );
      });

      return data || [];
    } catch (error) {
      console.error('Error in getSmartQuestions:', error);
      return this.getFallbackQuestions(categoryId, difficultyId, normalizeQuestionLocale(languageCode), limit);
    }
  }

  /**
   * Fallback question selection when smart selection fails
   */
  private async getFallbackQuestions(
    categoryId: number,
    difficultyId: number,
    languageCode: string,
    limit: number
  ): Promise<any[]> {
    // Use the existing get_questions_for_quiz_mixed function
    const normalizedLanguageCode = normalizeQuestionLocale(languageCode);

    const { data, error } = await supabase.rpc('get_questions_for_quiz_mixed', {
      p_category_id: categoryId,
      p_difficulty_id: difficultyId,
      p_language_code: normalizedLanguageCode,
      p_limit: limit
    });

    if (error) {
      console.error('Fallback question selection failed:', error);
      return [];
    }

    // Filter out session questions client-side
    const filtered = (data || []).filter((q: any) => 
      !this.sessionQuestionIds.has(q.question_id)
    );

    // If not enough questions after filtering, use all available
    const questions = filtered.length >= limit ? filtered.slice(0, limit) : data?.slice(0, limit) || [];

    // Add to session tracking
    questions.forEach((q: any) => {
      this.addQuestionToSession(
        q.question_id,
        categoryId,
        difficultyId,
        q.question_type
      );
    });

    return questions;
  }

  /**
   * Complete the current session
   */
  async completeSession(
    correctAnswers: number,
    totalScore: number,
    timeSpent: number
  ): Promise<boolean> {
    if (!this.currentSession) {
      return false;
    }

    // Skip database update for local sessions
    if (this.currentSession.sessionId.startsWith('local_')) {
      this.clearSession();
      return true;
    }

    try {
      const { error } = await supabase.rpc('complete_quiz_session', {
        p_session_id: this.currentSession.sessionId,
        p_correct_answers: correctAnswers,
        p_total_score: totalScore,
        p_time_taken: timeSpent
      });

      if (error) {
        console.error('Error completing session:', error);
        return false;
      }

      this.clearSession();
      return true;
    } catch (error) {
      console.error('Error in completeSession:', error);
      this.clearSession();
      return false;
    }
  }

  /**
   * Check if a question has been shown in the current session
   */
  isQuestionInSession(questionId: number): boolean {
    return this.sessionQuestionIds.has(questionId);
  }

  /**
   * Get all questions shown in current session
   */
  getSessionQuestions(): number[] {
    return Array.from(this.sessionQuestionIds);
  }

  /**
   * Get session statistics
   */
  getSessionStats(): {
    totalQuestions: number;
    correctAnswers: number;
    averageTime: number;
    accuracy: number;
  } {
    const questions = Array.from(this.sessionQuestions.values());
    const answeredQuestions = questions.filter(q => q.answeredCorrectly !== undefined);
    const correctAnswers = answeredQuestions.filter(q => q.answeredCorrectly).length;
    const totalTime = answeredQuestions.reduce((sum, q) => sum + (q.timeSpent || 0), 0);
    
    return {
      totalQuestions: questions.length,
      correctAnswers,
      averageTime: answeredQuestions.length > 0 ? totalTime / answeredQuestions.length : 0,
      accuracy: answeredQuestions.length > 0 ? (correctAnswers / answeredQuestions.length) * 100 : 0
    };
  }

  /**
   * Clear the current session
   */
  clearSession(): void {
    this.currentSession = null;
    this.sessionQuestions.clear();
    this.sessionQuestionIds.clear();
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSession?.sessionId || null;
  }

  /**
   * Check if there's an active session
   */
  hasActiveSession(): boolean {
    return this.currentSession !== null;
  }
}

// Export singleton instance
export const sessionQuestionService = new SessionQuestionService();

// Export types for use in other modules
export type { SessionQuestionService };
